import {makeObservable, observable, action} from 'mobx';
import {FAQType} from 'js/containers/PostTypes/SelfLearning/LearningPath/LearningPath';

export type TabData = {
    description: string,
    filterTabs: Array<{
        ufid: string,
        tabTitle: string,
        faq: Array<FAQType>
    }>
}

type MainTabType = 'first_item' | 'second_item';

class LearningPathStore {
    lastClickedElement:string = '';
    showFirstTabCompleteMessage = true;
    showSecondTabCompleteMessage = true;
    firstTabData:TabData = null;
    secondTabData: TabData = null;
    activeMainTab:MainTabType = 'first_item';
    activeFilterTab:string = '';
    firstTabCompleteState:boolean = null;
    secondTabCompleteState:boolean = null;
    activeTabData:TabData = null;
    faqStatusList: Array<{
        faq_id: string,
        status: boolean
    }>;
    activeFAQContent:Array<FAQType> = null;

    constructor() {
        makeObservable(this, {
            lastClickedElement:observable,
            showFirstTabCompleteMessage:observable,
            showSecondTabCompleteMessage:observable,
            setShowFirstTabCompleteMessage: action,
            setShowSecondTabCompleteMessage: action,
            firstTabData: observable,
            secondTabData: observable,
            activeMainTab: observable,
            activeTabData: observable,
            activeFilterTab: observable,
            firstTabCompleteState: observable,
            secondTabCompleteState: observable,
            activeFAQContent: observable,
            faqStatusList: observable,
            setFirstTabCompleteState:action,
            completeItemOnChange:action,
            setSecondTabCompleteState:action,
            getGroupCompletedStatus:action,
            setFirstTabData:action,
            setSecondTabData:action,
            setActiveMainTab:action,
            setActiveFilterTab:action,
            setActiveTabData:action,
            setFAQStatusList:action,
            setCompleteInBulk:action,
            getEnv:action,
            getTag:action,
            setActiveFAQContent: action
        });
    }

    setFAQStatusList(data) {
        this.faqStatusList = data;
    }

    setShowFirstTabCompleteMessage(value) {
        this.showFirstTabCompleteMessage = value;
    }

    setShowSecondTabCompleteMessage(value) {
        this.showSecondTabCompleteMessage = value;
    }

    getEnv() {
        const {
            REACT_APP_LOCAL_LINK,
            REACT_APP_UAT_LINK,
            REACT_APP_PROD_LINK,
            REACT_APP_DEV_LINK
        } = process.env || {};
        if (window.location.origin === REACT_APP_PROD_LINK) {
            return 'prd';
        } else if (window.location.origin === REACT_APP_DEV_LINK) {
            return 'dev';
        } else if (window.location.origin === REACT_APP_UAT_LINK) {
            return 'uat';
        } else if (window.location.origin === REACT_APP_LOCAL_LINK) {
            return 'local';
        }
    }

    getTag(pageId, activeMainTab) {
        const tab = activeMainTab === 'first_item' ? 'tab1' : 'tab2';
        return `sl_${pageId}_${tab}_${this.getEnv()}`;
    }

    async setCompleteInBulk(postId, apiPath, faqIds) {
        return await fetch(`${apiPath}/graphql`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                query: `
					{
                        selfLearning {
                          sendBulkCompleted(
                          check: true, 
                          post_id: ${postId}, 
                          main_tab_id: "${this.activeMainTab}", 
                          filter_tab_id: "${this.activeFilterTab}", 
                          faq_ids: "${faqIds}") {
                            result
                          }
                        }
                    }
				`
            })
        });
    }

    async sendGRTag(tag, apiPath) {
        return await fetch(`${apiPath}/graphql`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                query: `
                    {
                        getResponse {
                          addTagToContact(tag: "${tag}")
                        }
                      }
				`
            })
        });
    }

    getAllCheckedFAQStatus() {
        return this.activeFAQContent?.every(id =>
            this.faqStatusList?.some(item =>
                item.faq_id === id?.ufid && item?.status === true
            )
        );
    }


    getIsSingleUncheckedFAQ(faqId: string) {
        if (!this.activeFAQContent?.length) {
            return false;
        }

        let falseCount = 0;
        let falseFaqId: string | null = null;

        for (const faq of this.activeFAQContent) {
            const isChecked = this.faqStatusList?.some(
                item => item.faq_id === faq?.ufid && item?.status === true
            );

            if (!isChecked) {
                falseCount = falseCount + 1;
                falseFaqId = faq?.ufid ?? null;

                if (falseCount > 1) {
                    return false;
                }
            }
        }

        return falseCount === 1 && falseFaqId === faqId;
    }

    getGroupCompletedStatus(statusList, tab: 1 | 2) {
        const tabFaqIdsList = [];
        let source = this.firstTabData;

        if (tab === 2) {
            source = this.secondTabData;
        }

        if (!source?.filterTabs || !tabFaqIdsList) {
            return false;
        }

        source?.filterTabs?.forEach(filterTab =>
            filterTab?.faq?.forEach((faqItem) => {
                tabFaqIdsList.push(faqItem?.ufid);
            })
        );
        return tabFaqIdsList?.every(id =>
            statusList?.some(item =>
                item.faq_id === id && item?.status === true
            )
        );
    }

    async sentGRTag(postId, apiPath) {
        const isTab1Completed = this.getGroupCompletedStatus(this.faqStatusList, 1);
        const isTab2Completed = this.getGroupCompletedStatus(this.faqStatusList, 2);

        if (isTab1Completed && this.activeMainTab === 'first_item') {
            await this.sendGRTag(this.getTag(postId, this.activeMainTab), apiPath);
        }

        if (isTab2Completed && this.activeMainTab === 'second_item') {
            await this.sendGRTag(this.getTag(postId, this.activeMainTab), apiPath);
        }
    }

    async checkAllActiveFAQItems(postId, apiPath) {
        const copy = [...this.faqStatusList];
        const items = [];
        this.activeFAQContent?.map((id) => {
            const index = copy?.findIndex(item => item.faq_id === id?.ufid);
            if (index !== -1) {
                items.push(copy[index]?.faq_id);
                copy[index].status = true;
            } else {
                items.push(id?.ufid);
                copy.push({
                    // eslint-disable-next-line babel/camelcase
                    faq_id: id?.ufid,
                    status: true
                });
            }
        });

        this.faqStatusList = copy;
        const result = items.join(',');

        this.lastClickedElement = '';

        await this.sentGRTag(postId, apiPath);

        await this.setCompleteInBulk(postId, apiPath, result);


    }

    getFAQItemStatus(faqId) {
        return this.faqStatusList?.find(item => faqId === item?.faq_id)?.status;
    }

    setActiveMainTab(value: 'first_item' | 'second_item') {
        return this.activeMainTab = value;
    }

    setFirstTabData(value:TabData) {
        this.firstTabData = value;
    }

    setSecondTabData(value:TabData) {
        this.secondTabData = value;
    }

    async completeItemOnChange(e, apiPath, postId, skipLastChecked = false) {
        const allChecked = this.getAllCheckedFAQStatus();
        const faqId = e?.currentTarget?.name;
        const check = e?.currentTarget?.checked;

        const faqListCopy = [...this.faqStatusList];
        const index = faqListCopy.findIndex(item => item.faq_id === faqId);

        if (!check && allChecked || this.lastClickedElement === faqId) {
            return;
        }

        if (!skipLastChecked) {
            const isLastChecked = this.getIsSingleUncheckedFAQ(faqId);

            if (isLastChecked) {
                this.lastClickedElement = faqId;
                return;
            }
        }

        if (!check) {
            if (index !== -1) {
                faqListCopy[index].status = check;
            } else {
                faqListCopy.push({
                    // eslint-disable-next-line babel/camelcase
                    faq_id: faqId,
                    status: check
                });
            }
        } else {
            if (index !== -1) {
                faqListCopy[index].status = check;
            } else {
                faqListCopy.push({
                    // eslint-disable-next-line babel/camelcase
                    faq_id: faqId,
                    status: check
                });
            }
        }

        this.faqStatusList = faqListCopy;

        await this.sentGRTag(postId, apiPath);

        return await fetch(`${apiPath}/graphql`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                query: `
					{
                        selfLearning {
                          sendCompleted(
                          check: ${check}, 
                          post_id: ${postId}, 
                          main_tab_id: "${this.activeMainTab}", 
                          filter_tab_id: "${this.activeFilterTab}", 
                          faq_id: "${faqId}") {
                            result
                          }
                        }
                    }
				`
            })
        });
    }

    setFirstTabCompleteState(value) {
        this.firstTabCompleteState = value;
    }

    setSecondTabCompleteState(value) {
        this.secondTabCompleteState = value;
    }

    setActiveTabData(index: 'first_item' | 'second_item') {
        this.activeTabData = index === 'first_item' ? this.firstTabData : this.secondTabData;
    }

    setActiveFilterTab(value: string) {
        this.activeFilterTab = value;
    }

    setActiveFAQContent() {
        this.activeFAQContent = this.activeTabData?.filterTabs
            .find(item => item?.ufid === this.activeFilterTab)?.faq;
    }
}
export default new LearningPathStore();
